import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Alert,
  CardHeader,
  Button,
} from 'reactstrap';
import Breadcrumbs from "components/Common/Breadcrumb";
import { fetchJenisSeleksiParentID, fetchJenisSeleksiByParentID } from '../../services/Master/MasterJenisSeleksi';
import {
  FiAlertCircle,
  FiUserCheck,
  FiArrowRight,
  FiBriefcase,
  FiBookOpen,
  FiUsers,
  FiShield,
  FiTarget,
  FiArrowLeft,
} from 'react-icons/fi';

const PaketRaportNasional = () => {
  const navigate = useNavigate();
  const [jenisSeleksiList, setJenisSeleksiList] = useState([]);
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pageTitle = "PAKET";

  const [currentParentId, setCurrentParentId] = useState(null);
  const [currentParentName, setCurrentParentName] = useState('PAKET');
  // Hapus state idTipeKontenFromUrl dan idJenisSeleksi, kita akan mengambilnya langsung dari URL di useEffect
  // const [idTipeKontenFromUrl, setIdTipeKontenFromUrl] = useState(null);
  // const [idJenisSeleksi, setJenisSeleksi] = useState(null); // Ini tidak digunakan

  useEffect(() => {
    document.title = `${currentParentName} | Kumpulan ${pageTitle}`;
  }, [pageTitle, currentParentName]);

  // Modifikasi: Hapus useEffect URL parsing yang terpisah
  // Karena kita akan memparsing URL langsung di useEffect fetching

  // Modifikasi utama: fetchJenisSeleksi akan menerima id, dan tipeKonten
  const fetchJenisSeleksi = useCallback(async (id, tipeKonten) => {
    try {
      setLoading(true);
      setError(null);
      
      const commonParams = {
        disable_pagination: true,
      };

      if (tipeKonten) { // Gunakan parameter tipeKonten yang diterima
        commonParams.id_tipe_konten = tipeKonten;
      }

      let response;
      if (id) {
        response = await fetchJenisSeleksiByParentID(id, commonParams); 
        
        if (response.success) {
            if (!response.data || response.data.length === 0) {
                // Pastikan id_tipe_konten juga diteruskan saat navigasi
                const navUrl = new URLSearchParams();
                navUrl.append('id_jenis_seleksi', id);
                if (tipeKonten) {
                    navUrl.append('id_tipe_konten', tipeKonten);
                }
                navigate(`/sub-paket?${navUrl.toString()}`);
                return; 
            }
        } else {
            setError(response.message || 'Gagal memuat data jenis seleksi');
            setJenisSeleksiList([]);
            return; 
        }
      } else {
        response = await fetchJenisSeleksiParentID(commonParams); 
      }

      if (response.success && Array.isArray(response.data)) {
        setJenisSeleksiList(response.data);
      } else {
        setError('Respons data tidak valid atau kosong.'); 
        setJenisSeleksiList([]);
      }
    } catch (err) {
      setError(err.message || 'Terjadi kesalahan saat memuat data');
      setJenisSeleksiList([]);
    } finally {
      setLoading(false);
    }
  }, [navigate]); // Dependencies hanya navigate

  // Modifikasi: useEffect ini akan menjadi satu-satunya pemicu fetching data
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tipeKontenFromUrl = params.get('id_tipe_konten');
    const kategoriPaketFromUrl = params.get('kategori_paket'); // Gunakan kategori_paket untuk nama
    
    // Perbarui currentParentName berdasarkan URL
    setCurrentParentName(kategoriPaketFromUrl ? kategoriPaketFromUrl.toUpperCase() : 'PAKET TRYOUT');

    // Panggil fetchJenisSeleksi dengan currentParentId dan tipeKontenFromUrl
    fetchJenisSeleksi(currentParentId, tipeKontenFromUrl);

  }, [fetchJenisSeleksi, currentParentId, location.search]); // location.search ditambahkan

  const handleCardClick = (id, name) => {
    // Saat klik kartu, kita tidak lagi mengubah currentParentId untuk memicu fetch di halaman yang sama.
    // Kita langsung navigasi.
    const params = new URLSearchParams(location.search);
    const tipeKontenFromUrl = params.get('id_tipe_konten'); // Ambil id_tipe_konten saat ini dari URL

    const navUrl = new URLSearchParams();
    navUrl.append('id_jenis_seleksi', id);
    if (tipeKontenFromUrl) {
        navUrl.append('id_tipe_konten', tipeKontenFromUrl);
    }
    // Jika Anda ingin nama seleksi juga di URL sub-paket:
    navUrl.append('kategori_paket', name); 
    
    navigate(`/sub-paket-raport?${navUrl.toString()}`);
  };
  
  const handleBackClick = () => {
    // Saat kembali, atur currentParentId ke null untuk kembali ke kategori utama
    setCurrentParentId(null);
    // currentParentName akan diatur ulang oleh useEffect yang memparsing URL
    // atau jika tidak ada id_tipe_konten di URL, akan kembali ke default
  };

  const getJenisSeleksiStyling = (jenisSeleksi) => {
    let icon = <FiUserCheck />;
    let color = '#007bff';
    let backgroundColor = '#e6f2ff';
    let iconBgColor = '#ffffff';
    const lowerJenisSeleksi = jenisSeleksi ? jenisSeleksi.toLowerCase() : '';

    if (lowerJenisSeleksi.includes('cpns')) {
      icon = <FiUsers />;
      color = '#28a745';
      backgroundColor = '#eaf7eb';
      iconBgColor = '#dff0d8'; 
    } else if (lowerJenisSeleksi.includes('kedinasan')) {
      icon = <FiBookOpen />;
      color = '#fd7e14';
      backgroundColor = '#fff3e6';
      iconBgColor = '#ffe3b3';
    } else if (lowerJenisSeleksi.includes('bumn') || lowerJenisSeleksi.includes('pppk')) {
      icon = <FiBriefcase />;
      color = '#6f42c1';
      backgroundColor = '#f2e8fa';
      iconBgColor = '#e6d8f5';
    } else if (lowerJenisSeleksi.includes('polri')) {
      icon = <FiShield />;
      color = '#004a7c';
      backgroundColor = '#e0ecf2';
      iconBgColor = '#cce3ef';
    } else if (lowerJenisSeleksi.includes('tni')) {
      icon = <FiTarget />;
      color = '#800000';
      backgroundColor = '#f7e0e0';
      iconBgColor = '#f2c8c8';
    }
    
    return { icon, color, backgroundColor, iconBgColor };
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Paket" breadcrumbItem={pageTitle} />
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
            <Spinner color="primary" />
            <span className="ms-2">Memuat jenis seleksi...</span>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Paket" breadcrumbItem={pageTitle} />
          <Alert color="danger">
            <FiAlertCircle className="me-2" size={18} />
            {error}
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {currentParentId ? (
          <Breadcrumbs title="Paket" breadcrumbItem={
            <Button 
                color="link" 
                className="p-0 text-primary" 
                onClick={handleBackClick}
            >
                <FiArrowLeft className="me-1" />
                Kembali ke Kategori Utama
            </Button>
          } />
        ) : (
          <Breadcrumbs title="Paket" breadcrumbItem={currentParentName} />
        )}
        
        {jenisSeleksiList.length === 0 ? (
          <Alert color="info" className="text-center py-3">
            {currentParentId ? 'Tidak ditemukan sub-kategori untuk jenis seleksi ini.' : 'Tidak ditemukan jenis seleksi utama.'}
          </Alert>
        ) : (
          <Row className="gy-4 mb-5">
            {jenisSeleksiList.map((item) => {
              const { icon, color, backgroundColor, iconBgColor } = getJenisSeleksiStyling(item.nama_seleksi);
              return (
                <Col key={item.id_jenis_seleksi} xl={4} lg={4} md={6}>
                  <Card 
                    className="tryout-card-v17 shadow-sm h-100 text-center"
                    onClick={() => handleCardClick(item.id_jenis_seleksi, item.nama_seleksi)}
                    style={{ cursor: 'pointer' }}
                  >
                    <CardHeader className="card-header-v17 p-3" style={{ backgroundColor: backgroundColor }}>
                      <div className="jenis-seleksi-wrapper-v17" style={{ color: color }}>
                        <div className="icon-circle-v17" style={{ backgroundColor: iconBgColor, borderColor: color }}>
                          {icon}
                        </div>
                        <h5 className="jenis-seleksi-title-v17 mb-0">{item.nama_seleksi}</h5>
                      </div>
                    </CardHeader>
                    <CardBody className="p-3 d-flex flex-column justify-content-between">
                      <p className="description-v17 mb-3">{item.deskripsi || 'Deskripsi tidak tersedia.'}</p>
                      <div className="btn btn-primary d-block w-100 btn-action-v17 mt-auto">
                        Lihat
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Container>
      <style>
        {`
          /* Card Styling - V17 */
          .tryout-card-v17 {
            transition: all 0.2s ease-out;
            border-radius: 10px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0,0,0,0.04);
            background-color: #ffffff;
            border: 1px solid #f2f2f2;
          }
          .tryout-card-v17:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          }
          .card-header-v17 {
            border-bottom: 1px solid #e0e0e0 !important;
            border-top-left-radius: 10px;
            border-top-right-radius: 10px;
            padding: 20px !important;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .jenis-seleksi-wrapper-v17 {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 100%;
          }
          .icon-circle-v17 {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 60px;
            height: 60px;
            border-radius: 50%;
            margin-bottom: 12px;
            border: 2px solid;
            font-size: 2.5rem;
            transition: transform 0.2s ease-in-out;
          }
          .tryout-card-v17:hover .icon-circle-v17 {
            transform: scale(1.1);
          }
          .icon-circle-v17 svg {
            font-size: 2.2rem;
            stroke-width: 1.5;
          }
          .jenis-seleksi-title-v17 {
            font-size: 1.25rem;
            font-weight: 700;
            line-height: 1.2;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: inherit;
            text-align: center;
          }
          .tryout-card-v17 .card-body {
            padding: 15px !important;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            text-align: center;
          }
          .description-v17 {
            font-size: 0.85rem;
            line-height: 1.4;
            color: #555;
            min-height: 40px;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
          }
          .btn-action-v17 {
            font-size: 0.85rem;
            font-weight: 600;
            padding: 0.5rem 1rem;
            border-radius: 6px;
            background-color: #007bff;
            border-color: #007bff;
            transition: all 0.2s ease;
            box-shadow: 0 2px 6px rgba(0,123,255,0.15);
          }
          .btn-action-v17:hover {
            background-color: #0056b3;
            border-color: #0056b3;
            transform: translateY(-1px);
            box-shadow: 0 3px 8px rgba(0,123,255,0.25);
          }
        `}
      </style>
    </div>
  );
};

export default PaketRaportNasional;